import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  getMessages,
  sendMessage,
  getConversations,
  getAvailableContacts,
  getBroadcastRecipients,
  sendBroadcastMessage,
  markRead,
  getGroupCandidates,
  createGroup,
  renameGroup,
  addGroupMembers,
  removeGroupMembers,
  sendGroupMessage,
  reportClientError,
} from '../services/moodle/chat';
import { useConfig } from './config';

export const ChatContext = createContext(null);

// Roster/unread-badge poll interval — independent of whether a conversation
// is open or the chat container is mounted (drives the floating button badge).
// Actual cadence is 20s; presence staleness is bounded by min(20s poll, refocus).
const ROSTER_POLL_INTERVAL_MS = 20000;

// Debounces a refocus-triggered roster refetch against a poll tick that just
// fired (or is about to). Long enough to swallow a concurrent poll tick and
// bound rapid tab-switching to one fetch per window; far shorter than the
// 20s cycle, so a refocus after real absence always refetches.
const REFOCUS_REFETCH_DEBOUNCE_MS = 5000;

export const ChatProvider = ({ children, courseId }) => {
  const { user } = useAuth();
  const { config } = useConfig();

  const [contacts, setContacts] = useState([]);
  // Broadcast ("Anuncios") conversations — read-only threads a student or
  // tutor is a member of. kind === 'broadcast'.
  const [broadcastConversations, setBroadcastConversations] = useState([]);
  // Group chat conversations — bidirectional, teacher-curated threads.
  // kind === 'group'. Distinct from broadcasts: composer stays open, any
  // member (including students) can post via sendGroupMessage.
  const [groupConversations, setGroupConversations] = useState([]);
  // Server-authoritative: whether the current user may broadcast in the
  // active course. Never inferred client-side — always sourced from
  // get_broadcast_recipients, same as every other role gate in this codebase.
  const [canBroadcast, setCanBroadcast] = useState(false);
  // Server-authoritative: whether the current user may create a group chat
  // in the active course. Same pattern as canBroadcast — never inferred
  // client-side, always sourced from get_group_candidates.
  const [canCreateGroup, setCanCreateGroup] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pollTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const selectedContactRef = useRef(null);
  // Generation guard: bumped every time courseId changes so in-flight async
  // responses from a previous course can detect they are stale and discard themselves.
  const requestCourseRef = useRef(0);
  // Debounce source of truth for refocus roster refetch — stamped by every
  // roster fetch source (mount, course change, 20s poll, sendBroadcast, refocus).
  const lastRosterFetchRef = useRef(0);
  const loadChatDataRef = useRef(null);
  const courseIdRef = useRef(null);
  const selectContactRef = useRef(null);
  const lastHandledNavRef = useRef('');
  const isChatDisabledRef = useRef(false);

  // Sync ref with state to avoid stale closure in polling loop
  selectedContactRef.current = selectedContact;
  courseIdRef.current = courseId;

  const loadChatData = useCallback(async (targetCourseId) => {
    if (!user || !user.token || !targetCourseId) return;

    lastRosterFetchRef.current = Date.now();

    // Capture the current generation. If courseId changes again before this
    // request resolves, requestCourseRef.current will no longer match.
    const requestId = requestCourseRef.current;

    setLoading(true);
    setError(null);
    try {
      // 1. Fetch contacts for this course
      const fetchedContacts = await getAvailableContacts(user.token, config.baseURL, targetCourseId);

      // 2. Fetch active conversations
      const activeConversations = await getConversations(user.token, config.baseURL, targetCourseId);

      // 3. Fetch the broadcast role gate + eligibility, eagerly and in parallel
      // with the reads above conceptually — canbroadcast must never be
      // inferred client-side. A failure here (e.g. plugin not enabled) must
      // not break the rest of chat loading, so it is isolated to false.
      let broadcastGate = false;
      try {
        const broadcastInfo = await getBroadcastRecipients(user.token, config.baseURL, targetCourseId);
        broadcastGate = !!(broadcastInfo && broadcastInfo.canbroadcast);
      } catch (broadcastErr) {
        console.error('Error checking broadcast eligibility:', broadcastErr);
        broadcastGate = false;
      }

      // 3b. Same pattern for the group-chat creation gate — never inferred
      // client-side, isolated failure never breaks the rest of chat loading.
      let groupGate = false;
      try {
        const groupInfo = await getGroupCandidates(user.token, config.baseURL, targetCourseId);
        groupGate = !!(groupInfo && groupInfo.cancreategroup);
      } catch (groupErr) {
        console.error('Error checking group-creation eligibility:', groupErr);
        groupGate = false;
      }

      // Discard stale response: the active course changed while these requests were in flight.
      if (requestCourseRef.current !== requestId) return;

      // 4. Map conversation IDs and unread counts to contacts
      const mappedContacts = fetchedContacts.map(contact => {
        const conv = activeConversations.find(c =>
          c.isdirect && c.members.some(m => Number(m.id) === Number(contact.id))
        );
        return {
          ...contact,
          conversationid: conv ? conv.id : null,
          unreadcount: conv ? conv.unreadcount : 0,
        };
      });

      // 5. Non-direct conversations (broadcasts + group chats). `kind` is
      // server-authoritative (see spec: Conversation Kind Discriminator).
      // Rollout safety: a legacy/partial deploy may omit `kind` entirely —
      // never assume 'group' in that case, fall back to the previous
      // conservative behavior (treat as broadcast/read-only).
      const resolveKind = (c) => {
        if (c.kind === 'group' || c.kind === 'broadcast' || c.kind === 'direct') {
          return c.kind;
        }
        return c.isdirect ? 'direct' : 'broadcast';
      };

      const nonDirect = activeConversations.filter(c => resolveKind(c) !== 'direct');
      const toRow = (c) => {
        const kind = resolveKind(c);
        return {
          id: c.id,
          conversationid: c.id,
          isGroup: true,
          isBroadcast: kind === 'broadcast',
          kind,
          fullname: c.name,
          subname: c.subname,
          members: c.members,
          unreadcount: c.unreadcount,
          lastmessagetime: c.lastmessagetime,
        };
      };
      const broadcasts = nonDirect.filter(c => resolveKind(c) === 'broadcast').map(toRow);
      const groups = nonDirect.filter(c => resolveKind(c) === 'group').map(toRow);

      setContacts(mappedContacts);
      setBroadcastConversations(broadcasts);
      setGroupConversations(groups);
      setCanBroadcast(broadcastGate);
      setCanCreateGroup(groupGate);

      // If we already had a selected contact, update it in state with new unread counts or IDs.
      // Guarded above, so this never re-reads a stale selectedContactRef from a discarded course.
      if (selectedContactRef.current) {
        const prevSelected = selectedContactRef.current;
        const updated = prevSelected.isGroup
          ? [...broadcasts, ...groups].find(g => g.id === prevSelected.id)
          : mappedContacts.find(c => c.id === prevSelected.id);
        if (updated) {
          setSelectedContact(updated);
        }
      } else {
        // Auto-select conversation with unread messages when coming from a notification
        const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        if (searchParams && searchParams.get('tab') === 'chat') {
          const targetUserId = searchParams.get('user') || searchParams.get('userId');
          const targetConvId = searchParams.get('conv') || searchParams.get('conversationId');

          let target = null;
          if (targetUserId) {
            target = mappedContacts.find(c => String(c.id) === String(targetUserId));
          } else if (targetConvId) {
            target = mappedContacts.find(c => String(c.conversationid) === String(targetConvId)) ||
                     groups.find(g => String(g.conversationid) === String(targetConvId)) ||
                     broadcasts.find(b => String(b.conversationid) === String(targetConvId));
          }

          if (!target) {
            const unreadContact = mappedContacts
              .filter(c => c.unreadcount > 0)
              .sort((a, b) => (b.unreadcount || 0) - (a.unreadcount || 0))[0];

            const unreadGroup = [...groups, ...broadcasts]
              .filter(g => g.unreadcount > 0)
              .sort((a, b) => (b.unreadcount || 0) - (a.unreadcount || 0))[0];

            target = unreadContact || unreadGroup;
          }

          if (target && selectContactRef.current) {
            lastHandledNavRef.current = window.location.search;
            selectContactRef.current(target);
          }
        }
      }
    } catch (err) {
      if (requestCourseRef.current !== requestId) return;
      const isChatDisabled = err?.message?.includes('chatdisabled') || err?.errorcode === 'error:chatdisabled';
      if (isChatDisabled) {
        // Chat is disabled on backend for this course; halt roster polling and clear contacts
        isChatDisabledRef.current = true;
        setContacts([]);
        setBroadcastConversations([]);
        setGroupConversations([]);
        return;
      }
      console.error('Error loading chat contacts:', err);
      setError(err);
    } finally {
      if (requestCourseRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [user, config]);

  loadChatDataRef.current = loadChatData;

  const fetchMessages = useCallback(async (conversationId) => {
    if (!user || !user.token || !conversationId || !courseId) return;

    try {
      const data = await getMessages(user.token, config.baseURL, conversationId, courseId);
      setMessages(prev => {
        if (data.length !== prev.length) {
          lastActivityRef.current = Date.now(); // update activity on message count change
        }
        return data;
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError(err);
    }
  }, [user, config, courseId]);

  const startPolling = useCallback((conversationId) => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    
    if (!conversationId) return;

    // Reset activity timer when starting
    lastActivityRef.current = Date.now();

    const poll = async () => {
      if (document.visibilityState === 'visible') {
        await fetchMessages(conversationId);
      }
      
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      let nextInterval = 10000; // default 10s

      if (timeSinceLastActivity < 60000) {
        nextInterval = 3000; // active chatting: 3s
      } else if (timeSinceLastActivity < 180000) {
        nextInterval = 6000; // 1-3 mins idle: 6s
      } else {
        nextInterval = 12000; // >3 mins idle: 12s
      }

      pollTimeoutRef.current = setTimeout(poll, nextInterval);
    };

    poll();
  }, [fetchMessages]);

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const selectContact = useCallback((contact) => {
    stopPolling();
    const cleanContact = contact ? { ...contact, unreadcount: 0 } : null;
    setSelectedContact(cleanContact);
    setMessages([]);

    if (contact && (contact.unreadcount > 0 || contact.conversationid)) {
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id || (contact.conversationid && c.conversationid === contact.conversationid)
            ? { ...c, unreadcount: 0 }
            : c
        )
      );
      setGroupConversations((prev) =>
        prev.map((g) =>
          g.id === contact.id || (contact.conversationid && g.conversationid === contact.conversationid)
            ? { ...g, unreadcount: 0 }
            : g
        )
      );
      setBroadcastConversations((prev) =>
        prev.map((b) =>
          b.id === contact.id || (contact.conversationid && b.conversationid === contact.conversationid)
            ? { ...b, unreadcount: 0 }
            : b
        )
      );
    }

    if (contact && contact.conversationid) {
      startPolling(contact.conversationid);

      // Fixes the unread-badge defect: the SPA previously never called the
      // existing mark_read endpoint, so unreadcount only ever incremented.
      if (user && user.token && courseId) {
        markRead(user.token, config.baseURL, contact.conversationid, courseId).catch(err => {
          console.error('Error marking conversation as read:', err);
        });

        if (contact.unreadcount > 0 && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tipddy:message_read', {
            detail: {
              courseId: Number(courseId),
              conversationId: contact.conversationid,
              unreadCount: contact.unreadcount || 1,
            }
          }));
        }
      }
    }
  }, [startPolling, stopPolling, user, config, courseId]);
  selectContactRef.current = selectContact;

  // Closes the active conversation without affecting the roster/unread poll.
  // Used by ChatContainer on unmount so the badge keeps working while the
  // container is closed.
  const closeConversation = useCallback(() => {
    setSelectedContact(null);
    setMessages([]);
    stopPolling();
  }, [stopPolling]);

  const sendChatMessage = useCallback(async (text) => {
    const contact = selectedContactRef.current;
    if (!user || !user.token || !contact || !text.trim() || !courseId) return;

    // Group chats (kind === 'group') are bidirectional but already have a
    // conversationid from creation — no lazy-create branch needed, and they
    // must dispatch through send_group_message, never send_message.
    if (contact.isGroup && contact.kind === 'group') {
      if (!contact.conversationid) return;
      try {
        await sendGroupMessage(user.token, config.baseURL, contact.conversationid, text);
        lastActivityRef.current = Date.now();
        await fetchMessages(contact.conversationid);
      } catch (err) {
        console.error('Error sending group chat message:', err);
        reportClientError(user.token, config.baseURL, courseId, 'send_group_message', err.message);
        setError(err);
        throw err;
      }
      return;
    }

    try {
      const result = await sendMessage(user.token, config.baseURL, contact.id, text, courseId);
      lastActivityRef.current = Date.now(); // update activity on message sent

      // Backend now returns the conversationid directly, so a new conversation
      // no longer needs a delayed refetch to discover its id.
      if (!contact.conversationid && result && result.conversationid) {
        const updatedContact = { ...contact, conversationid: result.conversationid };
        setSelectedContact(updatedContact);
        setContacts(prev => prev.map(c => c.id === contact.id ? updatedContact : c));
        startPolling(result.conversationid);
        await fetchMessages(result.conversationid);
      } else {
        await fetchMessages(contact.conversationid);
      }
    } catch (err) {
      console.error('Error sending chat message:', err);
      reportClientError(user.token, config.baseURL, courseId, 'send_message', err.message);
      setError(err);
      throw err;
    }
  }, [user, config, courseId, fetchMessages, startPolling]);

  // Sends a broadcast and refreshes chat data so the new conversation shows
  // up in broadcastConversations without waiting for the next roster poll.
  // Authorization is fully server-side (can_broadcast()); this is a thin
  // wrapper, same shape as sendChatMessage.
  const sendBroadcast = useCallback(async (targetCourseId, touserids, text) => {
    if (!user || !user.token) throw new Error('Not authenticated');

    try {
      const result = await sendBroadcastMessage(user.token, config.baseURL, targetCourseId, touserids, text);
      await loadChatData(targetCourseId);
      return result;
    } catch (err) {
      reportClientError(user.token, config.baseURL, targetCourseId, 'send_broadcast', err.message);
      throw err;
    }
  }, [user, config, loadChatData]);

  // Creates a teacher-curated group chat and refreshes chat data so the new
  // group shows up in groupConversations without waiting for the next roster
  // poll. Authorization is fully server-side (can_create_group()); this is a
  // thin wrapper, same shape as sendBroadcast.
  const createGroupChat = useCallback(async (targetCourseId, name, userids) => {
    if (!user || !user.token) throw new Error('Not authenticated');

    try {
      const result = await createGroup(user.token, config.baseURL, targetCourseId, name, userids);
      await loadChatData(targetCourseId);
      return result;
    } catch (err) {
      reportClientError(user.token, config.baseURL, targetCourseId, 'create_group', err.message);
      throw err;
    }
  }, [user, config, loadChatData]);

  // Group edit: rename and/or membership changes applied as one operation,
  // followed by a single roster refresh (three separate reloads would make
  // the list flicker three times for one save).
  //
  // Order matters and is not arbitrary: removals run before additions so a
  // tutor swapping members in a group already at the 40-member cap does not
  // trip error:toomanymembers on the way through. Each call is skipped when
  // it has nothing to do — the backend rejects empty member arrays and an
  // empty name outright.
  const updateGroupChat = useCallback(async (targetCourseId, conversationId, changes) => {
    if (!user || !user.token) throw new Error('Not authenticated');

    const { name = null, addIds = [], removeIds = [] } = changes || {};

    try {
      if (name) {
        await renameGroup(user.token, config.baseURL, conversationId, name);
      }
      if (removeIds.length > 0) {
        await removeGroupMembers(user.token, config.baseURL, conversationId, removeIds);
      }
      if (addIds.length > 0) {
        await addGroupMembers(user.token, config.baseURL, conversationId, addIds);
      }

      await loadChatData(targetCourseId);
    } catch (err) {
      reportClientError(user.token, config.baseURL, targetCourseId, 'update_group', err.message);
      throw err;
    }
  }, [user, config, loadChatData]);

  // Visibility Hook to pause polling on window defocus
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        if (selectedContactRef.current?.conversationid) {
          startPolling(selectedContactRef.current.conversationid);
        }
        // Presence is only as fresh as the last roster read; waiting up to 20s
        // after the user comes back is exactly the reported repro.
        if (courseIdRef.current &&
            Date.now() - lastRosterFetchRef.current >= REFOCUS_REFETCH_DEBOUNCE_MS) {
          loadChatDataRef.current(courseIdRef.current);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  // Course-change reset: whenever courseId changes, this provider is the single
  // shared session owner, so it must drop the previous course's session state
  // (selected contact, messages, contacts) and stop the previous message poll
  // before loading the new course's data. Uses an explicit effect rather than
  // key={courseId} because this provider's subtree covers the entire course
  // page, not just the chat UI (see design.md Decision 1).
  useEffect(() => {
    isChatDisabledRef.current = false;
    requestCourseRef.current += 1;
    lastHandledNavRef.current = '';
    setSelectedContact(null);
    setMessages([]);
    setContacts([]);
    setBroadcastConversations([]);
    setGroupConversations([]);
    setCanBroadcast(false);
    setCanCreateGroup(false);
    setError(null);
    stopPolling();

    if (courseId) {
      loadChatData(courseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Auto-select conversation with unread messages if URL navigates with tab=chat
  useEffect(() => {
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    if (!searchParams || searchParams.get('tab') !== 'chat') return;

    const navKey = window.location.search;
    if (navKey === lastHandledNavRef.current && selectedContact) return;

    if (!contacts.length && !groupConversations.length && !broadcastConversations.length) return;

    const targetUserId = searchParams.get('user') || searchParams.get('userId');
    const targetConvId = searchParams.get('conv') || searchParams.get('conversationId');

    let target = null;
    if (targetUserId) {
      target = contacts.find(c => String(c.id) === String(targetUserId));
    } else if (targetConvId) {
      target = contacts.find(c => String(c.conversationid) === String(targetConvId)) ||
               groupConversations.find(g => String(g.conversationid) === String(targetConvId)) ||
               broadcastConversations.find(b => String(b.conversationid) === String(targetConvId));
    }

    if (!target) {
      const unreadContact = contacts
        .filter(c => c.unreadcount > 0)
        .sort((a, b) => (b.unreadcount || 0) - (a.unreadcount || 0))[0];

      const unreadGroup = [...groupConversations, ...broadcastConversations]
        .filter(g => g.unreadcount > 0)
        .sort((a, b) => (b.unreadcount || 0) - (a.unreadcount || 0))[0];

      target = unreadContact || unreadGroup;
    }

    if (target) {
      lastHandledNavRef.current = navKey;
      selectContact(target);
    }
  }, [contacts, groupConversations, broadcastConversations, selectContact, selectedContact]);

  // Roster/unread-badge poll: runs independently of whether a conversation is
  // selected or ChatContainer is mounted, so the floating button badge stays
  // accurate even when the chat UI is closed. Independent timer from the
  // adaptive message poll above (see design.md Decision 2).
  useEffect(() => {
    if (!courseId) return undefined;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !isChatDisabledRef.current) {
        loadChatData(courseId);
      }
    }, ROSTER_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [courseId, loadChatData]);

  // Telemetry: Global unhandled promise rejection and uncaught error listeners scoped strictly to Tipddy.
  // Filters out any noise from Moodle core or third-party scripts.
  useEffect(() => {
    if (!courseId || !user?.token || !config?.baseURL) return undefined;

    const isTipddyRelated = (err, extra = '') => {
      const msg = err?.message || (typeof err === 'string' ? err : '');
      const stack = err?.stack || '';
      const combined = `${msg} ${stack} ${extra}`;
      return /tipddy|local_tipddy|ChatContext|chat\.js|MessageInput|MessageList|BroadcastComposer/i.test(combined);
    };

    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (!reason) return;
      const url = reason?.config?.url || '';
      if (!isTipddyRelated(reason, url)) return;

      const errorMsg = reason?.message || (typeof reason === 'string' ? reason : 'Unknown unhandled promise rejection');
      reportClientError(
        user.token,
        config.baseURL,
        courseId,
        'unhandled_rejection',
        errorMsg
      );
    };

    const handleGlobalError = (event) => {
      const error = event?.error;
      const filename = event?.filename || '';
      if (!isTipddyRelated(error, filename)) return;

      const errorMsg = event?.message || error?.message || 'Unknown uncaught script error';
      reportClientError(
        user.token,
        config.baseURL,
        courseId,
        'uncaught_error',
        errorMsg
      );
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
    };
  }, [courseId, user, config]);

  const value = {
    contacts,
    broadcastConversations,
    groupConversations,
    canBroadcast,
    canCreateGroup,
    selectedContact,
    messages,
    loading,
    error,
    loadChatData,
    selectContact,
    closeConversation,
    sendChatMessage,
    sendBroadcast,
    createGroupChat,
    updateGroupChat,
    stopPolling
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
